import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { tasksApi } from '../services/api/tasks';
import { queryKeys } from '../lib/queryKeys';

/**
 * THUB-231: shared task actions for every surface that renders <TaskDetails>.
 *
 * Before this hook, ConnectionDetails and PartnerDetails wired TaskDetails'
 * onExecute / onDelete / onToggleStatus callbacks to `console.log` + a local
 * refetch. The user confirmed a deletion in TaskDetails' modal, the modal
 * closed, and NOTHING happened — the task was never deleted and Disable never
 * changed status. Only TaskList (components/tasks/TaskList.tsx) had real
 * implementations.
 *
 * Why plain callbacks and not TanStack `useMutation`: this codebase uses
 * TanStack for READS ONLY — there is not a single `useMutation` in web/src.
 * Every write is `await api.x()` followed by a manual refresh/invalidate (see
 * hooks/useTaskManagement.ts). Introducing a mutation layer in a bug-fix slate
 * would be a brand-new pattern with a different error surface. This hook
 * follows the existing convention and just removes the triplication.
 *
 * `onEdit` deliberately lives OUTSIDE this hook: it is modal state (each
 * surface renders its own <TaskEditor>, which needs `partners` and
 * `onValidateConfiguration`), not an API call.
 */
export interface UseTaskActionsOptions {
  /**
   * Caller's local (non-TanStack) refresh — e.g. ConnectionDetails'
   * `refreshDependencies`. The detail surfaces hold their task lists in
   * component-local useState, so `invalidateQueries` alone cannot reach them.
   */
  onChanged?: () => void | Promise<void>;
}

export interface UseTaskActionsReturn {
  /** Manually trigger a task run. Toasts on success and failure. */
  executeTask: (taskId: string) => Promise<void>;
  /**
   * Enable or disable a task. `enabled` is the DESIRED state.
   * Implements the full THUB-149 two-phase enable: the first `enableTask` call
   * is a probe — when held files exist the backend returns
   * `requiresConfirmation` and does NOT enable, so we confirm with the user and
   * re-call with `deliverHeld: true`.
   */
  toggleTaskStatus: (taskId: string, enabled: boolean) => Promise<void>;
  /**
   * Delete a task. `version` feeds the backend's optimistic-locking check and
   * must be the version of the task actually being deleted.
   */
  deleteTask: (taskId: string, version?: number) => Promise<void>;
  /** True while a delete is in flight — pass to TaskDetails' `isDeleting`. */
  isDeleting: boolean;
  /** Message from a failed delete — pass to TaskDetails' `deleteError`. */
  deleteError: string | null;
  /** Clear a stale delete error (e.g. when reopening the modal). */
  clearDeleteError: () => void;
}

export function useTaskActions(options: UseTaskActionsOptions = {}): UseTaskActionsReturn {
  const { onChanged } = options;
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * Invalidate the global list caches, then run the caller's local refresh.
   *
   * `connections.all` is invalidated alongside `tasks.all` on purpose: a
   * connection record carries a cached `taskCounts`, which THUB-180 recomputes
   * server-side on task create/delete/enable/disable. Without this key the
   * freshly-corrected count sits in DynamoDB while the browser keeps showing
   * the old one. `partners.all` for the same reason (PartnerRecord.taskCounts).
   */
  const invalidateAndRefresh = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.connections.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    await onChanged?.();
  }, [queryClient, onChanged]);

  const executeTask = useCallback(async (taskId: string) => {
    try {
      await tasksApi.executeTaskManually(taskId);
      toast.success('Task execution started');
      await invalidateAndRefresh();
    } catch (error) {
      console.error('Failed to execute task:', error);
      const message = error instanceof Error ? error.message : 'Failed to execute task';
      toast.error(message);
    }
  }, [invalidateAndRefresh]);

  const toggleTaskStatus = useCallback(async (taskId: string, enabled: boolean) => {
    try {
      if (enabled) {
        // THUB-149 phase 1: probe. If files were held while the task was
        // disabled, the backend asks for confirmation and does NOT enable yet.
        const probe = await tasksApi.enableTask(taskId);
        if (probe.requiresConfirmation) {
          const auto = probe.autoFlushCount ?? 0;
          const meta = probe.withMetadataCount ?? 0;
          let message = `This task held ${probe.heldCount ?? 0} file(s) while disabled.\n\n`;
          message += auto > 0
            ? `Enabling will deliver ${auto} held file(s) now.`
            : `Enabling will not auto-deliver any held files.`;
          if (meta > 0) {
            message += `\n\n${meta} file(s) require metadata and must be sent manually `
              + `via the transfer dialog after enabling.`;
          }
          message += `\n\nContinue?`;
          if (!window.confirm(message)) {
            return; // user cancelled — task stays disabled, nothing to refresh
          }
          // THUB-149 phase 2: enable for real and flush the held files.
          const result = await tasksApi.enableTask(taskId, true);
          if (result.flushedCount) {
            toast.success(`Task enabled — delivering ${result.flushedCount} held file(s)`);
          } else {
            toast.success('Task enabled');
          }
        } else {
          toast.success('Task enabled');
        }
      } else {
        await tasksApi.disableTask(taskId);
        toast.success('Task disabled');
      }
      await invalidateAndRefresh();
    } catch (error) {
      console.error('Failed to toggle task status:', error);
      toast.error('Failed to update task status');
    }
  }, [invalidateAndRefresh]);

  const deleteTask = useCallback(async (taskId: string, version?: number) => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      // `version` drives the backend's optimistic-locking check — omitting it
      // is a silent runtime failure, not a compile error, which is exactly why
      // this lives in one shared place.
      await tasksApi.deleteTask(taskId, { version });
      toast.success('Task deletion in progress');
      await invalidateAndRefresh();
    } catch (error) {
      console.error('Failed to delete task:', error);
      const message = error instanceof Error
        ? error.message
        : 'Failed to delete task. Please try again.';
      setDeleteError(message);
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  }, [invalidateAndRefresh]);

  const clearDeleteError = useCallback(() => setDeleteError(null), []);

  return {
    executeTask,
    toggleTaskStatus,
    deleteTask,
    isDeleting,
    deleteError,
    clearDeleteError,
  };
}
